import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./index.css";
import "./App.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Verify from "./pages/Verify";
import Hub from "./pages/Hub";
import Dashboard from "./pages/Dashboard";
import Capture from "./pages/Capture";
import Library from "./pages/Library";
import StoryDetail from "./pages/StoryDetail";
import Admin from "./pages/Admin";
import PublicShare from "./pages/PublicShare";
import Impact from "./pages/Impact";
import ImpactDetail from "./pages/ImpactDetail";
import Kudos from "./pages/Kudos";
import Pricing from "./pages/Pricing";
import Facilitators from "./pages/Facilitators";
import FacilitatorBio from "./pages/FacilitatorBio";
import Resources from "./pages/Resources";
import Programs from "./pages/Programs";
import Pitches from "./pages/Pitches";
import TranslatedAccessible from "./pages/TranslatedAccessible";
import EmailCopilot from "./pages/EmailCopilot";
import FindLeads from "./pages/FindLeads";
import SavedLeads from "./pages/SavedLeads";
import ComingSoon from "./pages/ComingSoon";
import { Toaster } from "sonner";

function ProtectedRoute({ children, requireAdmin = false, bare = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  // requireAdmin means the admin queue page (super_admin only)
  if (requireAdmin && user.role !== "super_admin") return <Navigate to="/" replace />;
  // `bare` skips the app shell — used for client-shareable pages like the printable bio.
  if (bare) return children;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  // Client-facing custom domain, when accessed via yourculturestory.truecolorsintl.com,
  // every route lands on the public testimonial form (no auth, no app chrome).
  const isClientDomain = typeof window !== "undefined"
    && /(^|\.)yourculturestory\./i.test(window.location.hostname);
  if (isClientDomain) {
    return (
      <Routes>
        <Route path="*" element={<PublicShare />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="/share" element={<PublicShare />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/" element={<ProtectedRoute><Hub /></ProtectedRoute>} />
      <Route path="/stories" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
      <Route path="/capture" element={<ProtectedRoute><Capture /></ProtectedRoute>} />
      <Route path="/capture/:id" element={<ProtectedRoute><Capture /></ProtectedRoute>} />
      <Route path="/story/:id" element={<ProtectedRoute><StoryDetail /></ProtectedRoute>} />
      <Route path="/impact" element={<ProtectedRoute><Impact /></ProtectedRoute>} />
      <Route path="/impact/:id" element={<ProtectedRoute><ImpactDetail /></ProtectedRoute>} />
      <Route path="/kudos" element={<ProtectedRoute><Kudos /></ProtectedRoute>} />
      <Route path="/facilitators" element={<ProtectedRoute><Facilitators /></ProtectedRoute>} />
      <Route path="/facilitators/:id/bio" element={<ProtectedRoute bare><FacilitatorBio /></ProtectedRoute>} />
      <Route path="/resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
      <Route path="/pitches" element={<ProtectedRoute><Pitches /></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
      <Route path="/programs" element={<ProtectedRoute><Programs /></ProtectedRoute>} />
      <Route path="/translated-accessible" element={<ProtectedRoute><TranslatedAccessible /></ProtectedRoute>} />
      <Route path="/email-copilot" element={<ProtectedRoute><EmailCopilot /></ProtectedRoute>} />
      <Route path="/find-leads" element={<ProtectedRoute><FindLeads /></ProtectedRoute>} />
      <Route path="/saved-leads" element={<ProtectedRoute><SavedLeads /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
